import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Infinity as InfinityIcon, Lock, Timer, ChevronLeft, Footprints } from "lucide-react";

const TRACK_BG = "https://images.unsplash.com/photo-1770698693897-ee89c1f7aea2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxydW5uaW5nJTIwdHJhY2slMjBuaWdodCUyMHN0YWRpdW18ZW58MHx8fHwxNzg1MzE5MjQ3fDA&ixlib=rb-4.1.0&q=85";

/**
 * Endurance Mode entry page — unlocked after the player clears iw_10
 * (the final curated level). Shows total miles, best time and a big Run
 * button that hands off to the existing Play.jsx via /play/endurance/run.
 */
export default function Endurance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/endurance/info");
        setInfo(data);
      } catch (e) {
        setInfo({ unlocked: false, journey_completed: 0, journey_total_levels: 50, miles: 0 });
      }
    })();
  }, []);

  if (!info) {
    return <div data-testid="endurance-loading" className="p-8 text-center text-[#5F7161]">…</div>;
  }

  if (!info.unlocked) {
    const remaining = (info.journey_total_levels || 50) - (info.journey_completed || 0);
    return (
      <div data-testid="endurance-locked" className="max-w-xl mx-auto py-12 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#EFF1EC] flex items-center justify-center mb-4">
          <Lock size={28} strokeWidth={1.4} className="text-[#5F7161]" />
        </div>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-3xl sm:text-4xl font-light mb-3 text-[#2C332A]">
          {t("endurance.locked_title")}
        </h1>
        <p className="text-[#2C332A]/70 mb-6 max-w-md mx-auto">
          {t("endurance.locked_subtitle", { remaining })}
        </p>
        <Button
          data-testid="endurance-go-journey"
          onClick={() => navigate("/journey")}
          className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white px-8"
        >
          {t("endurance.continue_journey")}
        </Button>
      </div>
    );
  }

  const best = info.best_time_sec;
  const bestStr = best != null ? `${Math.floor(best / 60)}:${String(best % 60).padStart(2, "0")}` : "—";
  const nextMile = (info.miles || 0) + 1;

  return (
    <div className="max-w-xl mx-auto">
      <div
        className="relative overflow-hidden rounded-3xl bg-[#1A1F19] text-[#F3F4F6] flex flex-col"
        style={{ minHeight: "calc(100dvh - 240px)" }}
      >
        {/* track texture + dark gradient */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.12] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: `url(${TRACK_BG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top,#1A1F19 12%, rgba(26,31,25,0.4) 55%, #1A1F19 100%)" }} aria-hidden="true" />
        {/* kinetic outline ENDURANCE */}
        <motion.p
          aria-hidden="true"
          className="absolute top-[18%] left-0 whitespace-nowrap font-black italic tracking-tighter text-white/[0.04] pointer-events-none select-none"
          style={{ fontFamily: "Outfit, sans-serif", fontSize: "4rem", lineHeight: 1 }}
          animate={{ x: ["0%", "-40%"] }}
          transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
        >
          ENDURANCE · ENDURANCE · ENDURANCE
        </motion.p>

        {/* header */}
        <div className="relative z-10 flex items-center justify-between p-4">
          <button
            data-testid="endurance-back"
            onClick={() => navigate("/dashboard")}
            className="w-9 h-9 grid place-items-center rounded-full bg-white/10 active:scale-90 transition-transform"
            aria-label="Back"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/50 font-bold flex items-center gap-1.5">
            <InfinityIcon size={14} className="text-[#E8A365]" /> {t("endurance.label")}
          </span>
          <span className="w-9" />
        </div>

        {/* centerpiece mile ring */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-3">
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 160, damping: 16 }}
            className="relative w-40 h-40 rounded-full grid place-items-center"
            style={{ border: "2px solid rgba(95,113,97,0.5)", boxShadow: "0 0 60px rgba(232,163,101,0.15), inset 0 0 40px rgba(232,163,101,0.06)" }}
          >
            <motion.span
              className="absolute inset-2 rounded-full border border-[#E8A365]/25"
              animate={{ opacity: [0.25, 0.6, 0.25] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/50">{t("endurance.miles")}</p>
              <p
                data-testid="endurance-miles"
                style={{ fontFamily: "Outfit, sans-serif" }}
                className="text-6xl font-black tracking-tighter text-[#E8A365] leading-none tabular-nums mt-1"
              >
                {info.miles}
              </p>
            </div>
          </motion.div>

          {/* glass stats bento */}
          <div className="grid grid-cols-2 gap-3 w-full max-w-[320px] mt-6">
            <div
              data-testid="endurance-best-time"
              className="rounded-2xl p-4 bg-[#2C332A]/60 backdrop-blur-xl border border-[#5F7161]/40"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 flex items-center gap-1"><Timer size={11} /> {t("endurance.best_time")}</p>
              <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-bold tabular-nums mt-1">{bestStr}</p>
            </div>
            <div className="rounded-2xl p-4 bg-[#2C332A]/60 backdrop-blur-xl border border-[#5F7161]/40">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 flex items-center gap-1"><Footprints size={11} /> {t("endurance.next_up", "Next up")}</p>
              <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-bold tabular-nums mt-1">{t("endurance.mile_n", "Mile {{n}}", { n: nextMile })}</p>
            </div>
          </div>
        </div>

        {/* sticky CTA */}
        <div className="relative z-10 px-6 pb-6 pt-3" style={{ background: "linear-gradient(to top,#1A1F19 40%, transparent)" }}>
          <motion.div
            animate={{ boxShadow: ["0 0 0 0 rgba(232,163,101,0.5)", "0 0 0 14px rgba(232,163,101,0)"] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="rounded-2xl"
          >
            <Button
              data-testid="endurance-run-btn"
              onClick={() => navigate("/play/endurance/run")}
              className="w-full h-12 rounded-2xl bg-[#E8A365] hover:bg-[#D89455] text-[#1A1F19] text-base font-black uppercase tracking-wide"
            >
              {t("endurance.next_mile", { n: nextMile })}
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
